import {
  Autocomplete,
  Button,
  Chip,
  Grid,
  styled,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  Box,
} from '@mui/material';
import {
  EmployeeEntity,
  PaymentMethodEntity,
  SectionEntity,
  TurnoverItemEntityStatusEnum,
  DepartmentEntity,
  EmployeeToSeasonEntityStatusTypeEnum,
} from 'api/generated';
import AppModal from 'components/AppModal';
import { H2, H3, H5, Small } from 'components/Typography';
import CalendarInput from 'components/input-fields/CalendarInput';
import { t } from 'i18next';
import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { departmentsEmployeesApi, employeesApi, turnoverApi, departmentsApi, paymentSectionsApi } from 'api';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useSeason } from 'contexts/SeasonContext';
import FlexBox from 'components/flexbox/FlexBox';
import toast, { Toaster } from 'react-hot-toast';
import CurrencyInput from 'react-currency-input-field';
import AppTextField from 'components/input-fields/AppTextField';
import { addDays, format, isSameDay } from 'date-fns';
import { translatePaymentTypes } from 'utils/convertPaymentTypes';
import { DeleteForever } from '@mui/icons-material';
import { CustomDialog } from 'components/CustomDialog';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DatePickerModal from './DatePickerModal';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { ignoreUTC } from 'utils/utils';

interface ModalProps {
  section?: SectionEntity;
  open: boolean;
  isOwner: boolean;
  isController: boolean;
  onClose: () => void;
  onBack?: () => void;
}


const StyledAppModal = styled(AppModal)(({ theme }) => ({
  width: useMediaQuery(theme.breakpoints.down('sm')) ? '100%' : 650,
  maxHeight: useMediaQuery(theme.breakpoints.down('sm')) ? '80%' : 800,
  overflowY: 'auto',
}));
const BetaChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  fontWeight: "bold",
  fontSize: "0.6rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
  height: "16px",
  "& .MuiChip-label": {
    padding: "0px 6px",
    fontSize: "0.6rem",
  },
}));


const AddTurnoverModal: FC<ModalProps> = ({
  open,
  onClose,
  onBack,
  section,
  isOwner,
  isController,
}) => {
  const [date, setDate] = useState(new Date());
  const [isDateValid, setIsDateValid] = useState(true);
  const [dateDepchiefError, setDateDepchiefError] = useState(false);

  const [itemOfToday, setItemOfToday] = useState<boolean>(false);
  const [itemOfYesterday, setItemOfYesterday] = useState<boolean>(false);

  const [items, setItems] = useState<
    Array<{
      value: number;
      stringValue?: string;
      id?: number;
      paymentMethod: PaymentMethodEntity;
    }>
  >([]);
  const { seasonId } = useSeason();
  const theme = useTheme();

  const [employees, setEmployees] = useState<EmployeeEntity[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeEntity>();
  const [status, setStatus] = useState<TurnoverItemEntityStatusEnum>();

  const [today, setToday] = useState(new Date());
  const [yesterday, setYesterday] = useState(addDays(new Date(), -1));

  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();

  const [isThereItem, setIsThereItem] = useState(false);

  const [canChangeTurnoversDates, setCanChangeTurnoversDates] = useState(false);

  const [total, setTotal] = useState(0);
  const [totalString, setTotalString] = useState('0');
  const [totalOverflowError, setTotalOverflowError] = useState(false);

  const [hasCash, setHasCash] = useState(false);


  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [openMoveItemsModal, setOpenMoveItemsModal] = useState(false);

  // Mance section deduction feature
  const [isManceSection, setIsManceSection] = useState(false);
  const [detractFromAnotherSection, setDetractFromAnotherSection] = useState(false);
  const [departments, setDepartments] = useState<DepartmentEntity[]>([]);
  const [selectedDepartmentForDeduction, setSelectedDepartmentForDeduction] = useState<DepartmentEntity | null>(null);
  const [sectionsForDeduction, setSectionsForDeduction] = useState<SectionEntity[]>([]);
  const [selectedSectionForDeduction, setSelectedSectionForDeduction] = useState<SectionEntity | null>(null);
  const [selectedMethodForDeduction, setSelectedMethodForDeduction] = useState<PaymentMethodEntity | null>(null);
  const [currentAmountInSelectedSection, setCurrentAmountInSelectedSection] = useState<number>(0);
  const [insufficientFunds, setInsufficientFunds] = useState(false);
  const [noSourceItem, setNoSourceItem] = useState(false);

  useEffect(() => {
    if (section && !isOwner) {
      setToday(new Date());
      setYesterday(addDays(new Date(), -1));
      turnoverApi.itemOfToday(seasonId, section.id).then(({ data }) => {
        setItemOfToday(data);
      });
      turnoverApi.itemOfYesterday(seasonId, section.id).then(({ data }) => {
        setItemOfYesterday(data);
      });
    }
  }, [section]);

  // Check if section is Mance and fetch departments
  useEffect(() => {
    if (section) {
      setIsManceSection(section.name?.toLowerCase().includes('mance') || false);
      if (section.name?.toLowerCase().includes('mance')) {
        // Fetch departments for deduction selection
        departmentsApi.findAll().then(({ data }) => {
          setDepartments(data);
        });
      }
    }
  }, [section, seasonId]);

  // Reset deduction selections when employee or section changes
  useEffect(() => {
    if (isManceSection && selectedEmployee) {
      setSelectedDepartmentForDeduction(null);
      setSelectedSectionForDeduction(null);
      setSelectedMethodForDeduction(null);
      setCurrentAmountInSelectedSection(0);
      setInsufficientFunds(false);
      setNoSourceItem(false);
    }
    if (!isManceSection) {
      setDetractFromAnotherSection(false);
      setSelectedDepartmentForDeduction(null);
      setSelectedSectionForDeduction(null);
      setSelectedMethodForDeduction(null);
      setCurrentAmountInSelectedSection(0);
      setInsufficientFunds(false);
      setNoSourceItem(false);
    }
  }, [selectedEmployee, isManceSection]);

  useEffect(() => {
    if (isController) {
      setMinDate(addDays(new Date(), -7));
      setMaxDate(new Date());
    } else {
      setMinDate(
        isOwner || canChangeTurnoversDates
          ? undefined
          : !itemOfYesterday && today.getHours() < 6
            ? yesterday
            : !itemOfToday
              ? today
              : undefined,
      );


      setMaxDate(
        isOwner || canChangeTurnoversDates
          ? undefined
          : !itemOfToday
            ? today
            : !itemOfYesterday && today.getHours() < 6
              ? yesterday
              : undefined,
      );
    }
    if (today.getHours() < 6) {
      setDate(yesterday);
    } else {
      setDate(today);
    }
  }, [itemOfToday, itemOfYesterday, today, yesterday, canChangeTurnoversDates, section]);

  useEffect(() => {
    if (
      !isOwner &&
      minDate &&
      maxDate &&
      (date.getFullYear() < minDate.getFullYear() ||
        (date.getFullYear() === minDate.getFullYear() &&
          date.getMonth() < minDate.getMonth()) ||
        (date.getFullYear() === minDate.getFullYear() &&
          date.getMonth() === minDate.getMonth() &&
          date.getDate() < minDate.getDate()) ||
        date.getFullYear() > maxDate.getFullYear() ||
        (date.getFullYear() === minDate.getFullYear() &&
          date.getMonth() > maxDate.getMonth()) ||
        (date.getFullYear() === minDate.getFullYear() &&
          date.getMonth() === minDate.getMonth() &&
          date.getDate() > maxDate.getDate()))
    ) {
      setDateDepchiefError(true);
    } else {
      setDateDepchiefError(false);
    }
  }, [date, minDate, maxDate]);

  const deleteTurnovers = async () => {
    for (let i = 0; i < values.length; i++) {
      const id = items[i]?.id;
      if (id) {
        await turnoverApi.remove(id);
      }
    };
    resetForm();
    onClose();
  }

  const moveTurnovers = async (newDate: Date) => {
    try {
      if (section && selectedEmployee) {
        await turnoverApi.moveTurnovers({
          params: {
            oldDate: date,
            newDate,
            sectionsId: section.id,
            employeeId: selectedEmployee.id
          }
        }).then(() => {
          setDate(newDate);
          toast.success(t('turnover.moved'))
        })
      }
    }
    catch {
      toast.error(t('turnoverModal.isThereItem'));
    }
  }

  // Handle department selection for deduction
  const handleDepartmentForDeductionSelect = (department: DepartmentEntity) => {
    setSelectedDepartmentForDeduction(department);
    setSelectedSectionForDeduction(null);
    setSelectedMethodForDeduction(null);
    setCurrentAmountInSelectedSection(0);

    // Fetch sections for the selected department
    paymentSectionsApi.findAll(seasonId).then(({ data }) => {
      const filteredSections = data.filter(s => s.departmentId === department.id);
      setSectionsForDeduction(filteredSections);
    });
  };

  // Handle section selection for deduction
  const handleSectionForDeductionSelect = (section: SectionEntity) => {
    setSelectedSectionForDeduction(section);
    setSelectedMethodForDeduction(null);
    setCurrentAmountInSelectedSection(0);
  };

  // Handle method selection for deduction
  const handleMethodForDeductionSelect = (method: PaymentMethodEntity) => {
    setSelectedMethodForDeduction(method);

    // Fetch current amount for the selected section, method, and employee
    if (selectedEmployee && selectedSectionForDeduction) {
      turnoverApi.findUnique(
        date.getTime(),
        selectedSectionForDeduction.id,
        method.id,
        seasonId,
        selectedEmployee.id
      ).then(({ data }) => {
        if (data) {
          setCurrentAmountInSelectedSection(data.value);
          setNoSourceItem(false);
        } else {
          setCurrentAmountInSelectedSection(0);
          setNoSourceItem(true);
        }
      }).catch(() => {
        setCurrentAmountInSelectedSection(0);
        setNoSourceItem(true);
      });
    }
  };

  const fetchData = async () => {
    if (section?.paymentMethods && selectedEmployee) {
      const temp: Array<{
        value: number;
        stringValue?: string;
        paymentMethod: PaymentMethodEntity;
        id?: number
      }> = [];

      // check if user can change date, if not then they do not need to check if there is an item on selected day
      let hasValues = false;
      let cashIndex: number | undefined;
      const canChangeTurnoversDates =
        !isOwner &&
        (await departmentsEmployeesApi
          .canChangeTurnoversDate(section?.departmentId ?? 0, seasonId)
          .then(({ data }) => {
            setCanChangeTurnoversDates(data);
            return data;
          }));

      let total = 0;
      // if it is not an Owner but an employee that can change turnover date then use dedicated query to get if there are item 
      if (canChangeTurnoversDates) {
        hasValues =
          (await turnoverApi.itemOfDay(date.getTime(), seasonId, section.id))
            .data;
      } else if (!isOwner) {
        if (today.getHours() > 6)
          hasValues = itemOfToday;
        else
          hasValues = itemOfYesterday;

      }
      for (const paymentMethod of section.paymentMethods) {
        let value = 0;
        let id: number | undefined;
        if (isOwner) {
          const turnoverItem = (
            await turnoverApi.findUnique(
              date.getTime(),
              section.id,
              paymentMethod.id,
              seasonId,
              selectedEmployee.id,
            )
          ).data;
          value = turnoverItem ? turnoverItem.value : 0.0;
          id = turnoverItem?.id;
          // if at least one turnover exists set isThereItem true 
          if (!hasValues)
            hasValues = id !== undefined;
          if (![
            'CASHDOWNPAYMENT',
            'CARDDOWNPAYMENT',
            'WIREDOWNPAYMENT',
            'PAYPALDOWNPAYMENT',
            'CHECKDOWNPAYMENT',
          ].includes(paymentMethod?.type)) {
            total += value;
          }
        }
        if (paymentMethod.type === 'CASH') cashIndex = temp.length;
        temp.push({
          value,
          stringValue: String(value),
          id,
          paymentMethod,
        });
      }
      // put cashIndex on top of the array
      if (cashIndex) {
        const switchingValues = temp[0]
        temp[0] = temp[cashIndex];
        temp[cashIndex] = switchingValues;
      }
      setHasCash(cashIndex !== undefined);
      setTotal(Number(total.toFixed(2)));
      setIsThereItem(hasValues);
      setTotalString(total.toFixed(2));
      setItems(temp);
    } else {
      setItems([]);
    }
    if (isOwner) {
      if (isController) {
        // For controllers, get all employees in the department, not just those with turnover
        employeesApi
          .findAllDepchief(seasonId, true, section?.departmentId)
          .then(({ data }) => {
            setEmployees(data);
          });
      } else {
        employeesApi
          .findAllDepchief(seasonId, true, section?.departmentId)
          .then(({ data }) => {
            setEmployees(data);
          });
      }
    } else {
      employeesApi.getMySelf().then(({ data }) => {
        setSelectedEmployee(data);
      });
    }
  };

  useEffect(() => {
    fetchData().then(() => {
      formik.resetForm();
    });
  }, [date, section?.id, selectedEmployee?.id]);

  useEffect(() => {
    if (isOwner)
      setStatus('CASHED');
  }, [open])

  const fieldValidationSchema = Yup.array()
    .of(
      Yup.object({
        value: Yup.number().required(t('value.notValid')),
      }),
    )
    .required(t('value.notValid'));

  const formik = useFormik({
    initialValues: items,
    enableReinitialize: true,
    validationSchema: fieldValidationSchema,
    onSubmit: (values) => {
      const createItem = (section: SectionEntity, i: number) => {
        turnoverApi
          .create(
            seasonId,
            (isOwner || isController) && selectedEmployee ? selectedEmployee.id : -1,
            {
              date: ignoreUTC(date).toISOString(),
              paymentMethodId: values[i].paymentMethod.id,
              paymentSectionId: section.id,
              value: values[i].value,
              status: status as any
            },
          )
          .then(async () => {
            // If deduction is enabled and this is the method being deducted from
            if (detractFromAnotherSection &&
              selectedMethodForDeduction &&
              selectedSectionForDeduction &&
              values[i].paymentMethod.id === selectedMethodForDeduction.id) {

              // Find the existing turnover item to deduct from
              if (selectedEmployee) {
                const existingItem = await turnoverApi.findUnique(
                  date.getTime(),
                  selectedSectionForDeduction.id,
                  selectedMethodForDeduction.id,
                  seasonId,
                  selectedEmployee.id
                );

                if (existingItem.data) {
                  // Update the existing item by reducing its value
                  const newValue = existingItem.data.value - values[i].value;
                  await turnoverApi.update(String(existingItem.data.id), { value: newValue });
                }
              }
            }

            onClose();
            if (isOwner || isController) {
              setSelectedEmployee(undefined);
            }
            setDate(new Date());
            resetForm();
          })
          .catch(() => {
            toast.error(t('turnover.dateErrorText'), {
              duration: 5000,
            });
          });
      };

      if (section && selectedEmployee) {
        for (let i = 0; i < values.length; i++) {
          if ((isOwner || isController) && (values[i] !== items[i] || values[i].value === 0)) {
            // if it is owner and a value exists, try to update it, otherwise create a new item
            if (items[i].id) {
              turnoverApi
                .update(String(items[i].id), { value: values[i].value })
                .then(() => {
                  onClose();
                  if (isOwner || isController) {
                    setSelectedEmployee(undefined);
                  }
                  setDate(new Date());
                  resetForm();
                })
                .catch(() => {
                  toast.error(t('turnover.dateErrorText'), {
                    duration: 5000,
                  });
                });
            } else {
              createItem(section, i);
            }
          } else {
            createItem(section, i);
          }
        }
      }
    },
  });

  const { values, handleSubmit, setFieldValue } = formik;

  const resetForm = () => {
    formik.resetForm();
    setTotal(0);
    setTotalString('0');
    setIsDeleteDialogOpen(false);
    setHasCash(false);
    setStatus(undefined);
    setDetractFromAnotherSection(false);
    setSelectedDepartmentForDeduction(null);
    setSelectedSectionForDeduction(null);
    setSelectedMethodForDeduction(null);
    setCurrentAmountInSelectedSection(0);
    setInsufficientFunds(false);
    setNoSourceItem(false);
  };

  useEffect(() => {
    if (hasCash !== undefined && hasCash) {
      let notCash = 0;
      values.forEach((value) => {
        if (
          ![
            'CASH',
            'CASHDOWNPAYMENT',
            'CARDDOWNPAYMENT',
            'WIREDOWNPAYMENT',
            'PAYPALDOWNPAYMENT',
            'CHECKDOWNPAYMENT',
          ].includes(value.paymentMethod?.type)
        )
          notCash += value.value;
      });
      const value = Number((total - notCash).toFixed(2));

      // sum of all values overflows total
      if (value < 0) setTotalOverflowError(true);
      else if (totalOverflowError) setTotalOverflowError(false);

      setFieldValue(`[${0}].stringValue`, value);
      setFieldValue(`[${0}].value`, value);
    } else {
      let total = 0;
      values.forEach((value) => {
        total += value.value;
      });
      setTotal(total);
      setTotalString(total.toString());
    }
  }, [total, values]);

  // Check for insufficient funds when deduction is enabled
  useEffect(() => {
    if (detractFromAnotherSection && selectedMethodForDeduction) {
      if (noSourceItem) {
        setInsufficientFunds(false); // Don't show insufficient funds if no source item
        return;
      }

      if (currentAmountInSelectedSection > 0) {
        const totalAmountToDeduct = values.reduce((sum, value) => {
          if (value.paymentMethod?.id === selectedMethodForDeduction.id) {
            return sum + value.value;
          }
          return sum;
        }, 0);

        const roundedTotal = Math.round(totalAmountToDeduct * 100) / 100;
        const roundedCurrent = Math.round(currentAmountInSelectedSection * 100) / 100;
        setInsufficientFunds(roundedTotal > roundedCurrent);
      } else {
        setInsufficientFunds(false);
      }
    } else {
      setInsufficientFunds(false);
    }
  }, [detractFromAnotherSection, selectedMethodForDeduction, currentAmountInSelectedSection, values, noSourceItem]);

  /* Mance section deduction feature */

  const getEmpStatus = useCallback((emp: EmployeeEntity) => {
    const seasonInfo = emp.employeesToSeasons?.find(s => s.seasonId === seasonId);
    const isActive = !!seasonInfo &&
      seasonInfo.statusType !== EmployeeToSeasonEntityStatusTypeEnum.Terminated &&
      !!seasonInfo.contractId;
    return isActive;
  }, [seasonId]);

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const aActive = getEmpStatus(a);
      const bActive = getEmpStatus(b);

      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      const lastNameComp = a.lastName.localeCompare(b.lastName);
      if (lastNameComp !== 0) return lastNameComp;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [employees, getEmpStatus]);

  return (
    <StyledAppModal
      open={open}
      handleClose={() => {
        onClose();
        if (isOwner || isController) {
          setSelectedEmployee(undefined);
        }
        setDate(new Date());
        resetForm();
      }}
      alignContent={'center'}
      alignItems={'center'}
    >
      <Toaster position='top-center' reverseOrder={false} />
      <CustomDialog
        open={isDeleteDialogOpen}
        onClose={() => { setIsDeleteDialogOpen(false) }}
        onConfirm={deleteTurnovers}
        title={t('notes.dialog.title')}
        content={t('turnovers.dialog.content')}
        confirmColor='error'
        cancelColor='primary'
        confirmText={t('notes.dialog.confirmText')}
        icon={<CheckCircleOutlinedIcon />}
      />
      <DatePickerModal
        onClose={() => { setOpenMoveItemsModal(false) }}
        onSubmit={moveTurnovers}
        open={openMoveItemsModal}
      />
      <Grid container alignItems={'center'} >
        <Grid xs={12} sm={8} item>
          <FlexBox alignItems="center" gap={1}>
            <H2 mb={1}>{t('amount.add')} </H2>
            {isController && <BetaChip label="BETA" size="small" />}
          </FlexBox>
          <H2>
            {section?.name} - {section?.taxPercentage}%
          </H2>
        </Grid>
        <Grid xs={6} sm={2} item my={isOwner && useMediaQuery(theme.breakpoints.down('sm')) ? 3 : 0}>
          {
            isOwner && !isController && selectedEmployee &&
            <Button variant='contained' disabled={!isThereItem} size='small' style={{
              justifyContent: 'center'
            }} onClick={() => { setOpenMoveItemsModal(true) }}
            >
              <EditCalendarIcon style={{
                display: 'flex'
              }} />
            </Button>
          }
        </Grid>
        <Grid xs={6} sm={2} item justifyContent={'right'} display={'flex'} my={isOwner && useMediaQuery(theme.breakpoints.down('sm')) ? 3 : 0}>
          {
            isOwner && !isController && selectedEmployee &&
            <Button variant='contained' color={isThereItem ? 'error' : undefined} disabled={!isThereItem} size='small' style={{
              justifyContent: 'center',
              display: 'flex'
            }}
              onClick={() => {
                setIsDeleteDialogOpen(true);
              }} >
              <DeleteForever style={{
                display: 'flex'
              }} />

            </Button>
          }
        </Grid>
      </Grid>
      {isOwner ||
        canChangeTurnoversDates /* ||
        (!itemOfToday && !itemOfYesterday  && today.getHours() < 6) */ ? (
          <CalendarInput
            sx={{ marginBottom: 2, marginTop: 1 }}
            format='dd.MM.yyyy'
            value={date}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
                setDate(new Date(newValue));
                setIsDateValid(true);
              } else setIsDateValid(false);
            }}
            slotProps={{
              textField: {
                helperText:
                  (!isDateValid && t('date.error')) ||
                  (dateDepchiefError && t('turnover.depchiefDateError')),
                error: !isDateValid || dateDepchiefError,
              },
            }}
            minDate={minDate}
            maxDate={maxDate}
            disableFuture={!isOwner}
          />
        ) : (
          <>
            <H2 marginTop={2} textAlign={'center'}>
              {format(date, 'dd/MM/yyyy')}
            </H2>
            {dateDepchiefError && (
              <H5 color={theme.palette.error.main} textAlign={'center'} mt={1}>
                {t('turnover.depchiefDateError')}
              </H5>
            )}
          </>
        )}
      {!isSameDay(date, today) && !dateDepchiefError && (
        <H5 marginBottom={3} color={theme.palette.warning.main}>
          {t('date.notToday')}
        </H5>
      )}
      {isOwner && (
        <Autocomplete
          sx={{ marginBottom: 2 }}
          value={selectedEmployee ?? null}
          options={sortedEmployees}
          groupBy={(option) => getEmpStatus(option) ? t('section.active') : t('section.terminated')}
          disabled={!isOwner || employees.length < 1}
          getOptionLabel={(employee) => {
            return employee.firstName + ' ' + employee.lastName;
          }}
          renderGroup={(params) => (
            <li key={params.key}>
              <Box
                sx={{
                  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#333',
                  padding: '8px 16px',
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                }}
              >
                {params.group}
              </Box>
              <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
            </li>
          )}
          renderInput={(params) => (
            <AppTextField
              {...params}
              label={t('employee.select')}
              inputProps={{
                ...params.inputProps,
              }}
              error={!selectedEmployee && isOwner}
              helperText={
                !selectedEmployee && isOwner && t('turnover.employeeError')
              }
            />
          )}
          onChange={(_event, value) => {
            if (value) {
              setSelectedEmployee(value);
            }
          }}
        />
      )}

      {isOwner && selectedEmployee && !isThereItem && (
        <Autocomplete
          sx={{ marginBottom: 2 }}
          value={status}
          options={Object.keys(TurnoverItemEntityStatusEnum).filter(value => value.toUpperCase() !== 'RECEIVED')}
          disabled={!isOwner || employees.length < 1}
          getOptionLabel={(status) => {

            return t('turnoverStatus.' + status.toLowerCase());
          }}
          renderInput={(params) => (
            <AppTextField
              {...params}
              label={t('status.label')}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
          onChange={(_event, value) => {
            if (value) {
              setStatus(value.toUpperCase() as any);
            }
          }}
        />
      )}

      {/* Mance section deduction feature */}
      {isManceSection && selectedEmployee && (
        <Box sx={{ marginBottom: 2, padding: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={detractFromAnotherSection}
                onChange={(e) => setDetractFromAnotherSection(e.target.checked)}
              />
            }
            label={t('turnover.detractFromAnotherSection')}
          />

          {detractFromAnotherSection && (
            <Box sx={{ marginTop: 2 }}>
              <Autocomplete
                sx={{ marginBottom: 2 }}
                value={selectedDepartmentForDeduction}
                options={departments}
                getOptionLabel={(department) => department.name}
                renderInput={(params) => (
                  <AppTextField
                    {...params}
                    label={t('turnover.selectDepartmentForDeduction')}
                  />
                )}
                onChange={(_event, value) => {
                  if (value) {
                    handleDepartmentForDeductionSelect(value);
                  }
                }}
              />

              {selectedDepartmentForDeduction && (
                <Autocomplete
                  sx={{ marginBottom: 2 }}
                  value={selectedSectionForDeduction}
                  options={sectionsForDeduction}
                  getOptionLabel={(section) => section.name}
                  renderInput={(params) => (
                    <AppTextField
                      {...params}
                      label={t('turnover.selectSectionForDeduction')}
                    />
                  )}
                  onChange={(_event, value) => {
                    if (value) {
                      handleSectionForDeductionSelect(value);
                    }
                  }}
                />
              )}

              {selectedSectionForDeduction && (
                <Autocomplete
                  sx={{ marginBottom: 2 }}
                  value={selectedMethodForDeduction}
                  options={selectedSectionForDeduction.paymentMethods || []}
                  getOptionLabel={(method) => translatePaymentTypes(method.type, t)}
                  renderInput={(params) => (
                    <AppTextField
                      {...params}
                      label={t('turnover.selectMethodForDeduction')}
                    />
                  )}
                  onChange={(_event, value) => {
                    if (value) {
                      handleMethodForDeductionSelect(value);
                    }
                  }}
                />
              )}

              {selectedMethodForDeduction && (
                <Box sx={{ marginTop: 1 }}>
                  {noSourceItem ? (
                    <Small color={theme.palette.error.main}>
                      {t('turnover.noSourceItem')}
                    </Small>
                  ) : currentAmountInSelectedSection > 0 ? (
                    <>
                      <H5>{t('turnover.currentAmount')}: €{currentAmountInSelectedSection.toFixed(2)}</H5>
                      {insufficientFunds && (
                        <Small color={theme.palette.error.main}>
                          {t('turnover.insufficientFunds')}
                        </Small>
                      )}
                    </>
                  ) : null}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {(!isThereItem || isOwner || isController) && selectedEmployee ? (
        <Grid container>
          {values.length > 0 && (
            <Grid item xs={12}>
              <H3 textOverflow={'ellipsis'}>{t('total')}</H3>
              <CurrencyInput
                style={{
                  width: '100%',
                  margin: 1,
                  borderRadius: 4,
                  position: 'relative',
                  backgroundColor: theme.palette.background.paper,
                  border: '1px solid #ced4da',
                  fontSize: 16,
                  padding: '10px 12px',
                  transition: theme.transitions.create([
                    'border-color',
                    'box-shadow',
                  ]),
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                  fontWeight: 500,
                  fontFamily: 'Montserrat',
                  color:
                    !hasCash
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                }}
                type='text'
                value={totalString}
                disabled={!hasCash}
                decimalsLimit={2}
                onValueChange={(value, _name, values) => {
                  setTotal(values?.float ?? 0);
                  setTotalString(value ?? '0');
                }}
                intlConfig={{
                  locale: 'de-DE',
                  currency: 'EUR',
                }}
                step={1}
              />
              {totalOverflowError && (
                <Small color={theme.palette.error.main}>
                  {t('paymentMethod.totalOverflow')}
                </Small>
              )}
            </Grid>
          )}
          {values && values.length > 0 ? (
            values.map((item, index) => {
              const disabled =
                item.paymentMethod?.type === 'CASH' ||
                (total === 0 && hasCash);
              return (
                <Grid item padding={1} xs={12} md={6}>
                  <Grid container marginTop={2} marginBottom={3}>
                    <Grid item xs={12}>
                      <H3 textOverflow={'ellipsis'}>
                        {translatePaymentTypes(
                          item.paymentMethod?.type ?? '',
                          t,
                        )}
                      </H3>
                      <Small
                        color={
                          item.paymentMethod?.tenantBankingInformation
                            ? theme.palette.text.primary
                            : theme.palette.background.default
                        }
                      >
                        {item.paymentMethod?.tenantBankingInformation
                          ?.bankName ?? '-'}
                      </Small>
                    </Grid>
                    <Grid item xs={12}>
                      <Small>
                        {item.paymentMethod?.description || '\u00A0'}
                      </Small>
                    </Grid>
                    <Grid item xs={12}>
                      <CurrencyInput
                        style={{
                          width: '100%',
                          margin: 1,
                          borderRadius: 4,
                          position: 'relative',
                          backgroundColor: theme.palette.background.paper,
                          border: '1px solid #ced4da',
                          fontSize: 16,
                          padding: '10px 12px',
                          transition: theme.transitions.create([
                            'border-color',
                            'box-shadow',
                          ]),
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                          fontWeight: 500,
                          fontFamily: 'Montserrat',
                          color: disabled
                            ? theme.palette.text.disabled
                            : theme.palette.text.primary,
                        }}
                        type='text'
                        value={item.stringValue}
                        disabled={disabled}
                        decimalsLimit={2}
                        onValueChange={(value, _name, values) => {
                          setFieldValue(`[${index}].stringValue`, value);
                          setFieldValue(`[${index}].value`, values?.float);
                        }}
                        intlConfig={{
                          locale: 'de-DE',
                          currency: 'EUR',
                        }}
                        step={1}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })
          ) : (
            <></>
          )}
          <>
            {(!values || values.length === 0) &&
              selectedEmployee &&
              t('paymentMethod.empty')}
          </>
          <>{!selectedEmployee && t('employee.select')}</>
        </Grid>
      ) : (isThereItem && !isOwner && !isController &&
        <Small color={theme.palette.warning.main}>
          {t('turnoverModal.isThereItem')}
        </Small>
      )}

      {(isOwner || isController) && (
        <Box 
          mt={3} 
          p={2} 
          bgcolor={theme.palette.mode === 'light' ? '#fff4e5' : 'rgba(255, 152, 0, 0.1)'} 
          border={`1px solid ${theme.palette.warning.main}`}
          borderRadius={1}
        >
          <H5 mb={1} color={theme.palette.warning.main}>{t('turnover.debug.title')}</H5>
          <Small display="block">{t('turnover.debug.reasons')}</Small>
          <ul style={{ margin: 0, paddingLeft: 20, color: theme.palette.text.secondary }}>
            {(isOwner && !selectedEmployee) && <li>{t('turnover.debug.missingEmployee')}</li>}
            {dateDepchiefError && <li>{t('turnover.debug.dateError')}</li>}
            {totalOverflowError && <li>{t('turnover.debug.totalOverflow')}</li>}
            {(isThereItem && !isOwner && !isController) && <li>{t('turnover.debug.itemExists')}</li>}
            {insufficientFunds && <li>{t('turnover.debug.insufficientFunds')}</li>}
            {(detractFromAnotherSection && noSourceItem) && <li>{t('turnover.debug.noSourceItem')}</li>}
            {!(
               (isOwner && !selectedEmployee) ||
               dateDepchiefError ||
               totalOverflowError ||
               (isThereItem && !isOwner && !isController) ||
               insufficientFunds ||
               (detractFromAnotherSection && noSourceItem)
            ) && <li>{t('turnover.debug.allClear')}</li>}
          </ul>
        </Box>
      )}

      <FlexBox justifyContent='flex-end' gap={2} marginTop={4}>
        {onBack && (
          <Button
            fullWidth
            variant='contained'
            color='inherit'
            onClick={() => {
              onBack();
              setDate(new Date());

              if (isOwner || isController) {
                setSelectedEmployee(undefined);
              }
              resetForm();
            }}
          >
            {t('back')}
          </Button>
        )}

        <Button
          fullWidth
          variant='contained'
          color='error'
          onClick={() => {
            onClose();
            if (isOwner || isController) {
              setSelectedEmployee(undefined);
            }
            setDate(new Date());
            resetForm();
          }}
        >
          {t('cancel')}
        </Button>

        <Button
          fullWidth
          disabled={
            (isOwner && !selectedEmployee) ||
            dateDepchiefError ||
            totalOverflowError ||
            (isThereItem && !isOwner && !isController) ||
            insufficientFunds ||
            (detractFromAnotherSection && noSourceItem)
          }
          variant='contained'
          onClick={() => {
            handleSubmit();
          }}
        >
          {t('save')}
        </Button>
      </FlexBox>
    </StyledAppModal>
  );
};

export default AddTurnoverModal;
